/**
 * The cadgaragepro.com assistant (v0.1.218; Sercan, 21 Oct 2026: "yapay zekayla konuşabilsin ziyaretçiler", live
 * answers without his approval, limited to what the site already says). POST /api/v1/site/chat, public, from the site
 * only: the visitor's message is stored as one turn, the worker answers it from the catalog and the approved knowledge
 * base only, and the API returns the answer within SITE_CHAT_WAIT_MS. No price promise, no discount, no measurement
 * that is not in the catalog; when unsure the assistant offers to pass the question to Sercan (the site's Ask form).
 */
import { z } from 'zod';
/** Longest visitor message. */
export const SITE_CHAT_MESSAGE_MAX = 600;
/** Messages one chat may hold; the widget then points to the Ask form. */
export const SITE_CHAT_TURNS_MAX = 20;
/** All chats together, per day (cost guard). */
export const SITE_CHAT_PER_DAY = 300;
/** One client address (IPv4, or an IPv6 /64), per day: one visitor cannot use up the day for everyone. */
export const SITE_CHAT_PER_ADDRESS_DAY = 25;
/** Messages one API process waits on at once; more get the hand-off answer at once. */
export const SITE_CHAT_WAITING_MAX = 12;
/** How long the API waits for the worker's answer. */
export const SITE_CHAT_WAIT_MS = 25_000;
/** Longest answer the widget shows. */
export const SITE_CHAT_REPLY_MAX = 1500;
/** At most this many catalog products an answer links to. */
export const SITE_CHAT_LINKS_MAX = 4;
export const siteChatBodySchema = z.strictObject({
    /** The chat's id from the previous answer; absent on the first message. */
    chatId: z.uuid().optional(),
    /** The page the visitor asks from (a path on the site). */
    page: z
        .string()
        .trim()
        .regex(/^\/[A-Za-z0-9/_.-]{0,199}$/),
    /** The product of that page, when it is a product page. */
    sku: z
        .string()
        .trim()
        .regex(/^[A-Z0-9-]{3,40}$/)
        .optional(),
    message: z.string().trim().min(1).max(SITE_CHAT_MESSAGE_MAX),
    /** Honeypot: hidden in the widget; a bot that fills it gets a polite answer and nothing is stored. */
    website: z.string().max(200).optional(),
});
export const siteChatResponseSchema = z.strictObject({
    chatId: z.uuid(),
    reply: z.string().max(SITE_CHAT_REPLY_MAX),
    /** Catalog products the answer names, by sku; the widget turns them into links to their pages. */
    skus: z.array(z.string().regex(/^[A-Z0-9-]{3,40}$/)).max(SITE_CHAT_LINKS_MAX),
    /** The assistant suggests passing the question to Sercan (the widget shows the Ask form). */
    handoff: z.boolean(),
    /** The chat reached SITE_CHAT_TURNS_MAX; the widget offers the Ask form instead of the input. */
    closed: z.boolean(),
});
/** What the worker's model must answer (JSON). */
export const siteChatAnswerSchema = z.strictObject({
    reply: z.string().min(1).max(SITE_CHAT_REPLY_MAX),
    skus: z.array(z.string()).max(SITE_CHAT_LINKS_MAX),
    handoff: z.boolean(),
    /** v0.1.248: the Turkish of the visitor's question and of the reply, for Sercan only (Telegram, panel). */
    question_tr: z.string().max(1200).optional(),
    reply_tr: z.string().max(3000).optional(),
});
/**
 * v0.1.248 (Sercan, 29 Oct 2026: "Sitede biri yazdığında görmek isterim"): a chat as Sercan reads it, in the panel's
 * Site sohbetleri list (GET /site-chats) and on Telegram (the bot reads GET /site-chats/{chatId}?from=turn). No client
 * hash, no address, no model, no cost.
 */
export const siteChatTurnViewSchema = z.strictObject({
    turnId: z.string().regex(/^\d+$/),
    at: z.iso.datetime({ offset: true }),
    question: z.string(),
    questionTr: z.string().nullable(),
    answer: z.string().nullable(),
    answerTr: z.string().nullable(),
    /** The assistant offered "Send to our team". */
    handoff: z.boolean(),
    /** answered, or not answered (failed, still pending). */
    answered: z.boolean(),
    /** Still being answered (pending or working): "cevap bekleniyor", not "cevap veremedi". */
    waiting: z.boolean(),
});
export const siteChatThreadSchema = z.strictObject({
    chatId: z.uuid(),
    /** The page of the chat's first turn. */
    page: z.string(),
    startedAt: z.iso.datetime({ offset: true }),
    /** The chat's first turn, also when ?from= leaves it out (the bot tells a first message from a later one). */
    firstTurnId: z.string().regex(/^\d+$/),
    turns: z.array(siteChatTurnViewSchema),
});
/**
 * v0.1.256 (Sercan, 1 Nov 2026: "site sohbetlerinde bunu görmüyorum"): a request sent with the site's form (a free small
 * CAD job, a project question, a notify-me) or with the assistant's "Send to our team". These are e-mail conversations
 * whose reply goes through Onaylar; the list shows them next to the chats so nothing from the site is missing here.
 */
export const siteFormRequestViewSchema = z.strictObject({
    conversationId: z.string().regex(/^\d+$/),
    at: z.iso.datetime({ offset: true }),
    kind: z.enum(['free_cad', 'question', 'notify', 'other']),
    /** Our own subject line, e.g. "Free CAD job (2021 Land Cruiser Prado) - CadGaragePro". */
    subject: z.string(),
    /** The visitor's words in Turkish when Twin translated them, else their own words. */
    textTr: z.string().nullable(),
    /** A reply went out. */
    answered: z.boolean(),
});
export const siteChatListSchema = z.strictObject({
    chats: z.array(siteChatThreadSchema),
    /** v0.1.256: the newest site form requests (first page only; empty on "Daha eski sohbetler"). */
    forms: z.array(siteFormRequestViewSchema),
    /** Older chats exist before the oldest one listed (GET /site-chats?before=startedAt). */
    more: z.boolean(),
});
export const siteChatListQuerySchema = z.strictObject({
    before: z.iso.datetime({ offset: true }).optional(),
});
export const siteChatThreadQuerySchema = z.strictObject({
    from: z
        .string()
        .regex(/^\d{1,18}$/)
        .optional(),
});
//# sourceMappingURL=site-chat.js.map